import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function displayDateForUI(val: Date): string {
  const now = new Date();
  const twelveHoursAgo = new Date(now.getTime() - 12 * 60 * 60 * 1000);

  if (val.toDateString() === now.toDateString() || val > twelveHoursAgo) {
    return val.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  } else {
    return val.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric'
    });
  }
}


export function formatDate(input: string | number): string {
  const date = new Date(input)
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

export const formatDateTime = (timestamp: string | number) => {
  // Convert to number if it's a string
  const numericTimestamp = 
    typeof timestamp === 'string' ? Number(timestamp) : timestamp;

  // Check if the timestamp is in seconds and convert to milliseconds
  const date = new Date(
    numericTimestamp < 1e12 ? numericTimestamp * 1000 : numericTimestamp
  );

  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  const ret = date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  });
  const formatted_ret = ret.replace(/,/g, '')
  return formatted_ret;
};

export const formatDateLong = (timestamp: string | number) => {
  // Convert to number if it's a string
  const numericTimestamp = 
    typeof timestamp === 'string' ? Number(timestamp) : timestamp;

  // Check if the timestamp is in seconds and convert to milliseconds
  const date = new Date(
    numericTimestamp < 1e12 ? numericTimestamp * 1000 : numericTimestamp
  );

  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();

  return `${weekday} ${month} ${day}, ${year}`;
};

export const formatTimeLong = (timestamp: string | number) => {
  // Convert to number if it's a string
  const numericTimestamp = 
    typeof timestamp === 'string' ? Number(timestamp) : timestamp;

  // Check if the timestamp is in seconds and convert to milliseconds
  const date = new Date(
    numericTimestamp < 1e12 ? numericTimestamp * 1000 : numericTimestamp
  );

  if (isNaN(date.getTime())) {
    return 'Invalid Time';
  }

  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).replace(' ', '');
  const timezone = date.toLocaleTimeString('en-US', {
    timeZoneName: 'short'
  }).split(' ').pop();

  return `${time} ${timezone}`;
};

export function getPrimaryExplorerUrl(chainId: number): string {
  switch (chainId) {
    case 1:
      return 'https://basescan.org';
    case 8453:
      return 'https://basescan.org';
    case 84532:
      return 'https://sepolia.basescan.org';
    default:
      return '';
  }
}

export function getPrimaryExplorerUrlForAddress(chainId: number, address: string): string {
  const baseUrl = getPrimaryExplorerUrl(chainId);
  if (!baseUrl) return '';
  return `${baseUrl}/address/${address}`;
}

export const zeroAddress = "0x0000000000000000000000000000000000000000";

export function absoluteUrl(path: string) {
  return `${process.env.NEXT_PUBLIC_APP_URL}${path}`
}

export function getCurrentTimestamp(): number {
  return new Date().getTime();
}

export function getDisplayNameFromFilename(filename: string): string {
  // Remove the file extension
  const nameWithoutExtension = filename.replace(/\.[^/.]+$/, "");

  // Replace dashes, underscores, and other similar characters with spaces
  const nameWithSpaces = nameWithoutExtension.replace(/[-_]+/g, " ");

  // Split the string into words and capitalize each word
  const words = nameWithSpaces.split(" ");
  const capitalizedWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1));

  // Join the capitalized words back into a single string
  const displayName = capitalizedWords.join(" ");

  return displayName;
}

export function formatEthAddress(address: string | null | undefined, chars: number = 3): string {
  if (!address || address.length < 10 || !address.startsWith('0x')) {
    return '0x...';
  }

  // Ensure chars is between 1 and 20
  const validatedChars = Math.max(1, Math.min(20, chars));

  // Ensure we don't try to show more characters than available (accounting for 0x prefix)
  const maxPossibleChars = Math.floor((address.length - 5) / 2); // -5 for "0x" + "..."
  const finalChars = Math.min(validatedChars, maxPossibleChars);

  return `${address.slice(0, finalChars + 2)}...${address.slice(-finalChars)}`;
}

export function updateUrlParams(params: {
  slap?: number | null;
  sticker?: number | null;
  player?: string | null;
  place?: number | null;
}) {
  if (typeof window === 'undefined') return;

  const urlParams = new URLSearchParams(window.location.search);

  // Clear existing params
  urlParams.delete('slap');
  urlParams.delete('sticker');
  urlParams.delete('player');
  urlParams.delete('place');

  // Add new params if they exist
  if (params.slap !== undefined && params.slap !== null) {
    urlParams.set('slap', params.slap.toString());
  }
  if (params.sticker !== undefined && params.sticker !== null) {
    urlParams.set('sticker', params.sticker.toString());
  }
  if (params.player !== undefined && params.player !== null) {
    urlParams.set('player', params.player);
  }
  if (params.place !== undefined && params.place !== null) {
    urlParams.set('place', params.place.toString());
  }

  // Update URL without page reload
  const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
  window.history.pushState({}, '', newUrl);
}
